import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";

import {
  findGetParameter,
  getData,
  postData,
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import {
  BCPBank,
  BCPCircleCheck,
  BCPEnvelope,
  BCPPlus,
  BCPTriangleExclamation,
} from "../../components/icons/bcp/BCPIcons";
import GenericPicker from "../../components/utilities/GenericPicker";
import { Formik } from "formik";
import * as Yup from "yup";
import InfoContainer from "../../components/layout/InfoContainer";
import { useNavigate } from "react-router";

export default function SquareConfirmation() {
  const [processing, setProcessing] = useState(true);
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [locations, setLocations] = useState([]);

  const code = findGetParameter("code");
  const state = findGetParameter("state");

  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const go = async () => {
      // Prevent multiple calls if already processing, this is a temporary fix
      if (!processing || account) {
        console.warn("SquareConfirmation: Already processing or account set");
        return;
      }

      try {
        if (!code || !state) {
          throw new Error("Missing required url parameters");
        }

        const apiResponse = await postData("/bankaccounts/square/confirm", {
          code: code,
          state: state,
        });

        // Get locations for the Square account
        if (apiResponse?.id && apiResponse?.gameStoreId) {
          const apiLocations = await getData(
            `/bankaccounts/square/${apiResponse.id}/locations`,
            {
              objectId: apiResponse?.gameStoreId,
              accountType: "gameStore",
            }
          );

          if (!apiLocations || apiLocations?.locations?.length === 0) {
            throw new Error("No locations found for the Square account");
          } else {
            // Format locations for the picker component
            const formattedLocations = apiLocations.locations.map((loc) => ({
              label: `${loc?.address?.address_line_1 ? loc.address.address_line_1 + ", " : ""}${loc?.address?.locality ? loc.address.locality + ", " : ""}${loc?.address?.administrative_district_level_1 ? loc.address.administrative_district_level_1 + ", " : ""}${loc?.address?.postal_code ? loc.address.postal_code : ""}${loc?.address?.country ? " (" + loc.address.country + ")" : ""}`,
              value: loc.id,
            }));

            setLocations(formattedLocations);
          }

          setStatus("Location");
          setAccount(apiResponse);
        } else {
          throw new Error("Error linking Square account");
        }
      } catch (err) {
        if (err?.status !== 500) {
          postNotification("error", err);
          setStatus("Error");
        }
      } finally {
        setProcessing(false);
      }
    };
    go();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Container sx={{ p: 2, pt: 2 }} maxWidth="lg">
          <Grid container sx={{ p: 2, gap: 2 }}>
            <Typography variant="h3" fontWeight="700" marginBottom={2}>
              Square Confirmation
            </Typography>

            <Grid item xs={12}>
              {status === "Location" && account && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h3">Select a location</Typography>
                  </Grid>

                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      Please select a location for your Square account from the
                      list below.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Formik
                      initialValues={{
                        location: "",
                      }}
                      enableReinitialize={true}
                      validationSchema={Yup.object().shape({
                        location: Yup.string().required(),
                      })}
                      onSubmit={async (values) => {
                        try {
                          setProcessing(true);

                          const response = await postData(
                            `/bankaccounts/square/${account.id}/updatelocation`,
                            {
                              locationId: values.location,
                              accountId: account.id,
                            }
                          );

                          if (
                            response?.message
                              ?.toLowerCase()
                              ?.includes("success")
                          ) {
                            postNotification(
                              "success",
                              "Square location set successfully!"
                            );
                            setStatus("Success");
                          } else {
                            throw new Error("Failed to update Square location");
                          }
                        } catch (err) {
                          postNotification("error", err);
                        } finally {
                          setProcessing(false);
                        }
                      }}
                    >
                      {({ values, handleChange, handleSubmit }) => (
                        <form onSubmit={handleSubmit}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              {locations?.length ? (
                                <GenericPicker
                                  name="location"
                                  label="Select Location"
                                  placeholder="Select a location"
                                  value={values.location}
                                  handleChange={handleChange}
                                  list={locations}
                                  required
                                  inclAny={false}
                                  fullWidth
                                />
                              ) : (
                                <GenericPicker
                                  label="Select Location"
                                  placeholder="Loading locations..."
                                  value={values.location}
                                  handleChange={handleChange}
                                  list={[]}
                                  required
                                  fullWidth
                                  disabled
                                />
                              )}
                            </Grid>

                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                fullWidth
                                disabled={!values.location}
                              >
                                Confirm Location
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              )}

              {status === "Success" && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InfoContainer
                      title={
                        <>
                          <BCPCircleCheck
                            color={theme?.palette?.success.main}
                          />{" "}
                          Success!
                        </>
                      }
                      body={`Square account successfully linked! You can now set up online ticket sales for your events.`}
                      ActionButton={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          {account?.gameStoreId && (
                            <Button
                              variant="contained"
                              onClick={(e) => {
                                navigate(
                                  `/organize/organization/${account.gameStoreId}?active_tab=banking`
                                );
                              }}
                              startIcon={<BCPBank />}
                            >
                              {"Bank Details"}
                            </Button>
                          )}

                          <Button
                            variant="outlined"
                            onClick={(e) => {
                              navigate("/organize/event");
                            }}
                            startIcon={<BCPPlus />}
                          >
                            {"Create Event"}
                          </Button>
                        </Box>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              )}

              {status === "Error" && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InfoContainer
                      title={
                        <>
                          <BCPTriangleExclamation
                            color={theme?.palette?.error.main}
                          />{" "}
                          Error - Payment Acccount Not Created
                        </>
                      }
                      body={`There was an error creating your Square account. Please try again later or contact support.`}
                      ActionButton={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          {account?.gameStoreId && (
                            <Button
                              variant="contained"
                              onClick={(e) => {
                                navigate(
                                  `/organize/organization/${account.gameStoreId}?active_tab=banking`
                                );
                              }}
                              startIcon={<BCPBank />}
                            >
                              {"Bank Details"}
                            </Button>
                          )}

                          <Button
                            variant="outlined"
                            onClick={(e) => {
                              navigate("/support");
                            }}
                            startIcon={<BCPEnvelope />}
                          >
                            {"Contact Support"}
                          </Button>
                        </Box>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {processing && <ModalLoadingFull />}
    </>
  );
}
